/**
 * vodInfoCache — localStorage cache for xtream get_vod_info responses.
 *
 * Cache key scope: server host + stream_id.
 *   - Shared across all users of the same server (same metadata for the same stream).
 *   - Different servers use different keys.
 *
 * Access check: checkVodAccess(streamId, userVodStreams) ensures the stream
 *   is actually in the user's current stream list before showing details.
 *   The list comes from getCachedVodStreams() which is already a 5-min
 *   in-memory cache, so the check is essentially free.
 *
 * TTL: 24 hours.
 * Inflight dedup: concurrent callers for the same key share one request.
 */

import xtreamAPI, { XtreamContent } from '../api/xtreamAPI';

const TTL_MS = 24 * 60 * 60 * 1000;

// In-flight dedup: host+streamId → pending Promise
const inflight = new Map<string, Promise<any>>();

function cacheKey(host: string, streamId: string): string {
  return `ott_vod_info_v1_${host}_${streamId}`;
}

function read(key: string): any | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: any; ts: number };
    if (Date.now() - ts > TTL_MS) { localStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
}

function write(key: string, data: any): void {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Drop-in replacement for `xtreamAPI.getVodInfo(streamId)`.
 * Returns null if the request fails.
 */
export async function getCachedVodInfo(streamId: string): Promise<any | null> {
  const host = xtreamAPI.getHost();
  const key = cacheKey(host, streamId);

  const hit = read(key);
  if (hit !== null) return hit;

  // Deduplicate concurrent calls for the same stream
  if (inflight.has(key)) return inflight.get(key)!;

  const promise = xtreamAPI.getVodInfo(streamId).then((data) => {
    if (data !== null && data !== undefined) write(key, data);
    inflight.delete(key);
    return data ?? null;
  }).catch((err) => {
    inflight.delete(key);
    throw err;
  });

  inflight.set(key, promise);
  return promise;
}

/**
 * Check whether a VOD stream is accessible to the current user.
 * Pass the result of getCachedVodStreams() as `userStreams`.
 * Returns true when the streamId appears in the user's stream list.
 */
export function checkVodAccess(streamId: string, userStreams: XtreamContent[]): boolean {
  const sid = String(streamId);
  return userStreams.some(s => String(s.stream_id ?? '') === sid);
}

/** Evict a single entry (e.g. after a user reports wrong data). */
export function evictVodInfoCache(streamId: string): void {
  const key = cacheKey(xtreamAPI.getHost(), streamId);
  try { localStorage.removeItem(key); } catch {}
}

export function clearVodInfoInflight(): void {
  inflight.clear();
}
